import { initDatePicker } from './date-picker.js';
import { clamp, formatMoneyInput, getMonthDate, parseMoney } from './form-values.js';
import { attachMoneyFormatting } from './input-format.js';

/** One row of the one-off overpayment list: month as YYYY-MM, amount in rubles (kopecks allowed). */
export type LumpEntry = { month: string; amount: number };

const DEFAULT_AMOUNT = 400_000;
const DEFAULT_OFFSET_MONTHS = 12;

const ROW_SELECTOR = '.lump-row';
const MONTH_SELECTOR = 'input[data-field="month"]';
const AMOUNT_SELECTOR = 'input[data-field="amount"]';

function listOf(form: HTMLFormElement): HTMLElement | null {
  return form.querySelector<HTMLElement>('#lump-list');
}

function rowsOf(form: HTMLFormElement): HTMLElement[] {
  return Array.from(form.querySelectorAll<HTMLElement>(`#lump-list ${ROW_SELECTOR}`));
}

function toggleOf(form: HTMLFormElement): HTMLInputElement | null {
  const control = form.elements.namedItem('lumpEnabled');
  return control instanceof HTMLInputElement ? control : null;
}

function field(caption: string, input: HTMLInputElement): HTMLLabelElement {
  const label = document.createElement('label');
  label.className = 'field';
  const span = document.createElement('span');
  span.textContent = caption;
  label.append(span, input);
  return label;
}

function buildRow(entry: LumpEntry): HTMLElement {
  const row = document.createElement('div');
  row.className = 'lump-row';

  const month = document.createElement('input');
  month.type = 'month';
  month.dataset.field = 'month';
  month.setAttribute('aria-label', 'Дата разового платежа');
  month.value = entry.month;

  const amount = document.createElement('input');
  amount.type = 'text';
  amount.setAttribute('inputmode', 'decimal');
  amount.dataset.money = '';
  amount.dataset.field = 'amount';
  amount.setAttribute('aria-label', 'Сумма разового платежа, ₽');
  amount.value = formatMoneyInput(entry.amount);

  const remove = document.createElement('button');
  remove.type = 'button';
  remove.className = 'table-btn lump-remove';
  remove.setAttribute('aria-label', 'Удалить платёж');
  remove.textContent = '×';

  row.append(field('Месяц', month), field('Сумма, ₽', amount), remove);
  return row;
}

function renumber(form: HTMLFormElement): void {
  rowsOf(form).forEach((row, index) => {
    row.dataset.index = String(index);
  });
}

/** Appends a row to `#lump-list` (with a desktop date picker); returns it, or `null` without the list. */
export function addLumpRow(form: HTMLFormElement, entry: LumpEntry): HTMLElement | null {
  const list = listOf(form);
  if (!list) return null;
  const row = buildRow(entry);
  list.appendChild(row);
  renumber(form);
  const month = row.querySelector<HTMLInputElement>(MONTH_SELECTOR);
  if (month) initDatePicker(month);
  const amount = row.querySelector<HTMLInputElement>(AMOUNT_SELECTOR);
  if (amount) attachMoneyFormatting(amount);
  return row;
}

/** Reads every row in DOM order; unparsable amounts read as 0. */
export function readLumps(form: HTMLFormElement): LumpEntry[] {
  return rowsOf(form).map(row => ({
    month: row.querySelector<HTMLInputElement>(MONTH_SELECTOR)?.value ?? '',
    amount: parseMoney(row.querySelector<HTMLInputElement>(AMOUNT_SELECTOR)?.value ?? ''),
  }));
}

/** Replaces the rows with `lumps` (in the given order). No-op without `#lump-list`. */
export function setLumps(form: HTMLFormElement, lumps: LumpEntry[]): void {
  const list = listOf(form);
  if (!list) return;
  list.replaceChildren();
  lumps.forEach(lump => addLumpRow(form, lump));
}

/**
 * Sensible row for a fresh list: one year after the loan start, clamped to the
 * last month of the term, 400 000 ₽.
 */
export function defaultLump(form: HTMLFormElement): LumpEntry {
  const startDate = (form.elements.namedItem('startDate') as HTMLInputElement | null)?.value || '2024-01';
  const termYears = parseFloat((form.elements.namedItem('termYears') as HTMLInputElement | null)?.value ?? '');
  const termMonths = Math.max(1, Math.round(clamp(termYears, 1, 40) * 12));
  const offset = Math.min(DEFAULT_OFFSET_MONTHS, termMonths - 1);
  return { month: getMonthDate(startDate, offset), amount: DEFAULT_AMOUNT };
}

function syncVisibility(form: HTMLFormElement): void {
  const fields = form.querySelector<HTMLElement>('#lump-fields');
  if (fields) fields.hidden = !toggleOf(form)?.checked;
}

/**
 * Wires the «Досрочно в выбранные месяцы» sheet: the toggle shows the list (adding one
 * default row when it is empty), «+ Ещё разовый платёж» appends a row, «×» removes
 * one (the list may end up empty). `onChange` runs after every add or remove;
 * edits inside rows bubble to the form as ordinary `input`/`change` events.
 */
export function initLumpList(form: HTMLFormElement, onChange: () => void): void {
  const list = listOf(form);
  if (!list) return;

  const ensureRow = () => {
    if (rowsOf(form).length === 0) addLumpRow(form, defaultLump(form));
  };

  toggleOf(form)?.addEventListener('change', () => {
    if (toggleOf(form)?.checked) ensureRow();
    syncVisibility(form);
  });

  form.querySelector<HTMLElement>('#lump-add')?.addEventListener('click', () => {
    addLumpRow(form, defaultLump(form));
    onChange();
  });

  list.addEventListener('click', event => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('.lump-remove');
    const row = button?.closest<HTMLElement>(ROW_SELECTOR);
    if (!button || !row || !list.contains(row)) return;
    row.remove();
    renumber(form);
    onChange();
  });

  if (toggleOf(form)?.checked) ensureRow();
  syncVisibility(form);
}
